import React, { createContext, useState, useEffect } from 'react';
import { jwtDecode as decode } from 'jwt-decode'
import Swal from "sweetalert2";
import axios from 'axios';


export const AuthContext = createContext();

const AuthContextProvider = (props) => {
  const [token, setToken] = useState('')
  const [isAuth, setIsAuth] = useState(false)
  const [user, setUser] = useState({})
  const [userType, setUserType] = useState('')
   const [paymentStatus, setPaymentStatus] = useState('')
  const [shouldShowPaymentWarning, setShouldShowPaymentWarning] = useState(false)

  // Function to check if current date is between 10th and end of month
  const isInPaymentPeriod = () => {
    const today = new Date();
    const currentDay = today.getDate();
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    return currentDay >= 10 && currentDay <= lastDayOfMonth;
  };

  // Function to check payment status from API
  const checkPaymentStatus = async () => {
    try {
      const token = localStorage.getItem('app_token');
      if (!token) {
        console.log('No token available for payment status check');
        return;
      }

      const response = await axios.get(import.meta.env.VITE_URL_SALDO_SISTEMIFY, {
        headers: {
          Authorization: `Bearer: ${token}`
        }
      });
      const { estado } = response.data;
      setPaymentStatus(estado);
      
      // Show warning if status is "Con Adeudo" and we're in payment period
      if (estado === "Con Adeudo" && isInPaymentPeriod()) {
        setShouldShowPaymentWarning(true);
      } else {
        setShouldShowPaymentWarning(false);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('');
      setShouldShowPaymentWarning(false);
    }
  };

  const loginUser = (token, type = 'user')=>{
    localStorage.setItem('app_token', token)
    localStorage.setItem('user_type', type)
    localStorage.setItem('tema', "white-content")
    const decoded = decode(token)
    setToken(token)
    setIsAuth(true)
    setUser(decoded)
    setUserType(type)
    // Check payment status after successful login (only for regular users, not providers)
    if (type !== 'provider') {
      checkPaymentStatus();
    }
  }

  const logoutUser = (user)=>{
    localStorage.removeItem('app_token')
    localStorage.removeItem('user_type')
    setToken({})
    setIsAuth(false)
    setUser(null)
    setUserType('')
    setPaymentStatus('')
    setShouldShowPaymentWarning(false)
  }

  const salir =()=>{
    const token = localStorage.getItem('app_token')
    const storedUserType = localStorage.getItem('user_type')
    
    if (!token) return;
    
    try {
      let dateNow = Math.floor(Date.now()/1000)
      const decoded = decode(token)
      let timexp = decoded.exp
      
      if (dateNow >= timexp){
        localStorage.removeItem('app_token')
        localStorage.removeItem('user_type')
        setToken({})
        setIsAuth(false)
        setUser(null)
        setUserType('')
        Swal.fire({
          position: "center",
          icon: "warning",
          title: "Tu sesion ha caducado",
          showConfirmButton: false,
          timer: 2000,
        });
        setTimeout(() => {
          if (storedUserType === 'provider') {
            window.location.href = '/provider-login';
          } else {
            window.location.href = '/login';
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Token validation error:', error);
      logoutUser();
    }
  }
  

  // Function to handle auth error from backend middleware
  const handleAuthError = () => {
    const storedUserType = localStorage.getItem('user_type');
    
    // Clear authentication state
    localStorage.removeItem('app_token');
    localStorage.removeItem('user_type');
    setToken({});
    setIsAuth(false);
    setUser(null);
    setUserType('');
    
    // Show user notification
    Swal.fire({
      position: "center",
      icon: "warning",
      title: "Tu sesión ha sido cerrada por seguridad",
      text: "Tu token de acceso ya no es válido",
      showConfirmButton: false,
      timer: 3000,
    });
    
    // Redirect to appropriate login page
    setTimeout(() => {
      if (storedUserType === 'provider') {
        window.location.href = '/provider-login';
      } else {
        window.location.href = '/Login';
      }
    }, 1000);
  };

  useEffect(()=>{
    const item = localStorage.getItem('app_token')
    const storedUserType = localStorage.getItem('user_type')
    
    // Set up axios response interceptor to handle auth errors
    const interceptor = axios.interceptors.response.use(
      (response) => {
        // If the response is successful, just return it
        return response;
      },
      (error) => {
        // Check if it's a 401 error with "Auth error" message from our backend middleware
        if (error.response &&
            error.response.status === 401 &&
            error.response.data &&
            error.response.data.message === 'Auth error') {
          
          // Handle the auth error automatically
          handleAuthError();
        }
        
        // Re-throw the error so other error handlers can still catch it if needed
        return Promise.reject(error);
      }
    );
    
    if (item){
      try {
        const decoded = decode(item)
        let dateNow = Math.floor(Date.now()/1000)
        let timexp = decoded.exp
        
        // Check if token is expired before setting state
        if (dateNow >= timexp) {
          localStorage.removeItem('app_token')
          localStorage.removeItem('user_type')
          setToken({})
          setIsAuth(false)
          setUser(null)
          setUserType('')
          Swal.fire({
            position: "center",
            icon: "warning",
            title: "Session expired",
            showConfirmButton: false,
            timer: 2000,
          });
          setTimeout(() => {
            const currentPath = window.location.pathname;
            if (currentPath.includes('provider')) {
              window.location.href = '/provider-login';
            } else {
              window.location.href = '/Login';
            }
          }, 1000);
        } else {
          // Token is valid, set the state
          setToken(item)
          setIsAuth(true)
          setUser(decoded)
          setUserType(storedUserType || 'user')
          
          // Check payment status when loading existing session (only for regular users, not providers)
          if (storedUserType !== 'provider') {
            checkPaymentStatus();
          }
        }
      } catch (error) {
        console.error('Token decode error:', error);
        localStorage.removeItem('app_token')
        localStorage.removeItem('user_type')
        setToken({})
        setIsAuth(false)
        setUser(null)
        setUserType('')
      }
    }
    
    // Cleanup interceptor when component unmounts
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [])

  return (
        <AuthContext.Provider
      value={{
        token,
        isAuth,
        user,
        userType,
        paymentStatus,
        shouldShowPaymentWarning,
        loginUser,
        logoutUser,
        checkPaymentStatus,
      }}
      >
      { props.children }
    </AuthContext.Provider>
  )
};

export default AuthContextProvider;